import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Delete,
  IconCheckmark,
  InfoButton,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { colors, Tooltip } from '@insurancemenu/imenu-components-lib';
import { AlignmentTypes, ICustomizableElement } from '@insurancemenu/imenu-components-lib/dist/types';
import { EngValue, MaxWaiver, PlanAttributeType } from 'types/productServiceV2';
import { cropCommonSuffixes } from 'global/utils/rfqHelper';
import { observer } from 'mobx-react-lite';
import {
  TitleStyled,
  AttributeHeaderStyled,
  AttributeAnswersStyled,
  AttributeListStyled,
  ProductOfferingStyled,
  LabelStyled,
  SectionStyled,
  MaxWaiverLabelStyled,
  AttributeCardStyled,
  AttributeCardWrapperStyled,
  RemoveAttributeButtonStyled,
  LabelKeyStyled,
} from './styles';
import {
  PLAN_ATTRIBUTES_LABEL_BTN_TEST_ID,
  ATTRIBUTE_ANSWERS_SECTION_TEST_ID,
  COVERED_ATTRIBUTE_SECTION_TEST_ID,
  MAX_WAIVER_NOTE_TEST_ID,
  NOT_COVERED_SECTION_TEST_ID,
} from '../constants';

export interface PlanAttributeBlockProps extends ICustomizableElement {
  label: string;
  headerElement?: JSX.Element;
  /**
   * An empty array indicates that 'not covered' was selected in the 'Benefit Covered' field.
   * undefined suggests that attribute information is not filled.
   * An array with values indicates that the attribute is covered, and the form is filled.
   */
  engValues: EngValue[] | undefined;
  planType: PlanAttributeType;
  removeAttrButtonStatus?: 'ENABLED' | 'DISABLED' | 'HIDDEN';
  planAttribute?: Record<string, string>;
  isAttributeOpenByDefault?: boolean;
  maxWaiverMessage?: MaxWaiver;
  onRemoveAttribute?: (label: string) => void;
}

interface RemoveAttributeButtonProps {
  status: 'ENABLED' | 'DISABLED' | 'HIDDEN';
  onRemoveAttribute?: () => void;
}

type CoverageAttrTextBlockProps = Pick<PlanAttributeBlockProps, 'engValues' | 'planAttribute' | 'label'>;
type AttributeNameIconProps = Pick<PlanAttributeBlockProps, 'planType'>;

const AttributeNameIcon = ({ planType }: AttributeNameIconProps): JSX.Element => {
  switch (planType) {
    case 'FILLED_UNCOVERED':
      return <IconCheckmark color={colors.neutral[500]} />;
    case 'FILLED_COVERED':
      return <IconCheckmark color={colors.green[600]} />;
    case 'UNFILLED':
    default:
      return <InfoButton color={colors.red[600]} />;
  }
};

const CoverageAttrTextBlock = observer(
  ({ engValues, planAttribute, label }: CoverageAttrTextBlockProps): JSX.Element | null => {
    if (!engValues) return null;
    const sortedEngValues = [...engValues].sort((a, b) => a.productOffering.localeCompare(b.productOffering));
    const productOfferings = cropCommonSuffixes(sortedEngValues?.map((value) => value.productOffering) || []);

    return (
      <>
        {sortedEngValues.map((engValue, index) => {
          const limitation = planAttribute ? planAttribute[engValue.limitationKey] : '';
          return (
            <React.Fragment key={`${label}_fragment_${engValue.productOffering}`}>
              <AttributeAnswersStyled data-test-id={ATTRIBUTE_ANSWERS_SECTION_TEST_ID}>
                {[...sortedEngValues].length > 1 && (
                  <ProductOfferingStyled>{productOfferings[index]}</ProductOfferingStyled>
                )}
                <LabelStyled>
                  <LabelKeyStyled>Coverage:</LabelKeyStyled> {engValue.label}
                </LabelStyled>
                {limitation && (
                  <LabelStyled>
                    <LabelKeyStyled>Limitation:</LabelKeyStyled> {limitation}
                  </LabelStyled>
                )}
              </AttributeAnswersStyled>
            </React.Fragment>
          );
        })}
      </>
    );
  },
);

const RemoveAttributeButton: React.FC<RemoveAttributeButtonProps> = ({ status, onRemoveAttribute }) => {
  return (
    <RemoveAttributeButtonStyled
      type="icon"
      color="custom"
      customColor={colors.red[600]}
      iconSize="3rem"
      disabled={status === 'DISABLED'}
      onClick={(): void => {
        if (onRemoveAttribute && status === 'ENABLED') onRemoveAttribute();
      }}
    >
      <Delete width="2.6rem" />
    </RemoveAttributeButtonStyled>
  );
};

const PlanAttributeBlock = observer(
  ({
    label,
    engValues,
    planAttribute,
    planType,
    isAttributeOpenByDefault,
    maxWaiverMessage,
    headerElement = <></>,
    className = '',
    removeAttrButtonStatus = 'HIDDEN',
    onRemoveAttribute,
  }: PlanAttributeBlockProps): JSX.Element => {
    const isAttributeInfoFilled = planType === 'FILLED_UNCOVERED' || planType === 'FILLED_COVERED';

    const [isSectionOpen, setIsSectionOpen] = useState(
      isAttributeOpenByDefault !== undefined ? isAttributeOpenByDefault : isAttributeInfoFilled,
    );

    return (
      <AttributeCardWrapperStyled className={className}>
        <AttributeCardStyled $planType={planType}>
          {/* Attribute Header */}
          <AttributeHeaderStyled>
            <TitleStyled
              type="text"
              iconSize={isAttributeInfoFilled ? '3rem' : '2.6rem'}
              leftIconProp={<AttributeNameIcon planType={planType} />}
              rightIconProp={isSectionOpen ? <ChevronUp /> : <ChevronDown />}
              onClick={(): void => setIsSectionOpen(!isSectionOpen)}
              disabled={!isAttributeInfoFilled}
              data-test-id={PLAN_ATTRIBUTES_LABEL_BTN_TEST_ID}
            >
              {label}
            </TitleStyled>
            {headerElement}
          </AttributeHeaderStyled>
          {/* Attribute body */}
          {/* Covered attribute section */}
          {planType === 'FILLED_COVERED' && (
            <SectionStyled $isOpen={isSectionOpen} data-test-id={COVERED_ATTRIBUTE_SECTION_TEST_ID}>
              <AttributeListStyled>
                {/* Eng Values Attributes */}
                <CoverageAttrTextBlock label={label} engValues={engValues} planAttribute={planAttribute} />
                {/* Max waiver block */}
                {maxWaiverMessage && maxWaiverMessage.benefits.includes(label) && (
                  <MaxWaiverLabelStyled data-test-id={MAX_WAIVER_NOTE_TEST_ID}>
                    <b>Max Waiver Note:</b> {maxWaiverMessage?.message}
                  </MaxWaiverLabelStyled>
                )}
              </AttributeListStyled>
            </SectionStyled>
          )}
          {/* Not covered attribute section */}
          {isAttributeInfoFilled && engValues?.length === 0 && (
            <SectionStyled $isOpen={isSectionOpen} data-test-id={NOT_COVERED_SECTION_TEST_ID}>
              <AttributeAnswersStyled>
                <LabelStyled>
                  <b>Coverage:</b> Not covered
                </LabelStyled>
              </AttributeAnswersStyled>
            </SectionStyled>
          )}
        </AttributeCardStyled>
        {removeAttrButtonStatus !== 'HIDDEN' &&
          (removeAttrButtonStatus === 'DISABLED' ? (
            <Tooltip alignment={AlignmentTypes.TOP_CENTER} label="Required attributes cannot be removed">
              <RemoveAttributeButton
                status={removeAttrButtonStatus}
                onRemoveAttribute={onRemoveAttribute && ((): void => onRemoveAttribute(label))}
              />
            </Tooltip>
          ) : (
            <RemoveAttributeButton
              status={removeAttrButtonStatus}
              onRemoveAttribute={onRemoveAttribute && ((): void => onRemoveAttribute(label))}
            />
          ))}
      </AttributeCardWrapperStyled>
    );
  },
);

export default PlanAttributeBlock;
